import * as React from "react";
import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const WORLD_LANGUAGES = [
  "Português",
  "Inglês",
  "Espanhol",
  "Francês",
  "Alemão",
  "Italiano",
  "Mandarim",
  "Japonês",
  "Coreano",
  "Russo",
  "Árabe",
  "Hindi",
  "Holandês",
  "Polonês",
  "Sueco",
  "Norueguês",
  "Dinamarquês",
  "Finlandês",
  "Grego",
  "Turco",
  "Hebraico",
  "Tailandês",
  "Vietnamita",
  "Indonésio",
  "Malaio",
  "Filipino/Tagalog",
  "Ucraniano",
  "Tcheco",
  "Húngaro",
  "Romeno",
  "Búlgaro",
  "Croata",
  "Sérvio",
  "Esloveno",
  "Eslovaco",
  "Catalão",
  "Basco",
  "Galego",
  "Persa/Farsi",
  "Urdu",
  "Bengali",
  "Punjabi",
  "Tâmil",
  "Telugu",
  "Canarim",
  "Gujarati",
  "Marata",
  "Africâner",
  "Suaíli",
  "Zulu",
  "Amárico",
  "Hausa",
  "Iorubá",
  "Igbo",
  "Cantonês",
  "Wu (Xangainês)",
  "Min",
  "Hakka",
  "Tibetano",
  "Mongol",
  "Cazaque",
  "Uzbeque",
  "Azerbaijano",
  "Georgiano",
  "Armênio",
  "Lituano",
  "Letão",
  "Estoniano",
  "Albanês",
  "Macedônio",
  "Bósnio",
  "Maltês",
  "Islandês",
  "Irlandês/Gaélico",
  "Galês",
  "Escocês",
  "Bretão",
  "Luxemburguês",
  "Libras (Língua Brasileira de Sinais)",
  "ASL (Língua de Sinais Americana)",
  "Latim",
  "Esperanto",
  "Catalão",
  "Nepali",
  "Birmanês",
  "Khmer",
  "Laosiano",
  "Sinhala",
  "Pashto",
  "Curdo",
  "Somali",
  "Malgaxe",
  "Tagalo",
];

function removeAccents(text: string): string {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

interface LanguageSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function LanguageSelector({
  value,
  onChange,
  disabled = false,
  placeholder = "Selecione o idioma",
  className,
}: LanguageSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredLanguages = useMemo(() => {
    if (!search) return WORLD_LANGUAGES;
    const searchNormalized = removeAccents(search.toLowerCase());
    return WORLD_LANGUAGES.filter((lang) =>
      removeAccents(lang.toLowerCase()).includes(searchNormalized)
    );
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover z-50">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar idioma..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>Idioma não encontrado.</CommandEmpty>
            <CommandGroup>
              {filteredLanguages.map((lang) => (
                <CommandItem
                  key={lang}
                  value={lang}
                  onSelect={() => {
                    onChange(lang);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === lang ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {lang}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
